import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import cx from "classnames";
import styles from "../scss/DigitalAdvice.module.scss";
import Icon from "../../common/components/Icon/Icon";
import Button from "../../common/components/Forms/Button/Button";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import ProgressBar from "../../common/components/ProgressBar/ProgressBar";
import { FeatureFlag } from "../../compare/enums/FeatureFlags";
import HelpSection from "../../common/components/HelpSection/HelpSection";
import {
    generateSOAPDF,
    getFinalRecommendedCover,
    getRecommendationDisclaimer,
    sendStatementOfAdvice
} from "../api/ApiDigitalAdvice";
import { AdviceControllerContext } from "../providers/DigitalAdviceProvider";
import RecommendedCover from "../components/RecommendedCover";
import {
    IInterimRecommendedCoverResults,
    IRCResults,
    IRecommendedCoverResults
} from "../interfaces/IDigitalAdviceData";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import DapCarousel from "../components/DapCarousel";
import DapRecommendationPlaceholder from "../components/Placeholder/DapRecommendationPlaceholder";
import useWindowDimensions from "../../common/hooks/useWindowDimensions";
import DapProceedDialog from "../components/Modals/DapProceedDialog";
import { convertBase64toBlob } from "../shared/digitalAdviceHelper";

export const OurAdvice: React.FC = () => {
    const history = useHistory();
    const search = new URLSearchParams(location.search);
    const sessionUserId = search.has("sessionUserId") && search.get("sessionUserId");
    const visitorId = search.has("visitorId") && search.get("visitorId");

    const handleBack = (): void => {
        history.goBack();
    };
    const { selectors } = useContext(AdviceControllerContext);

    const [frequency, setFrequency] = useState("Fortnight");
    const [isReady, setIsReady] = useState(true);
    const [isSOALoading, setIsSOALoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [recommendCount, setRecommendCount] = useState(0);
    const [recommendCover, setRecommendCover] = useState<IRecommendedCoverResults>();
    const { isTabletPortrait } = useWindowDimensions();
    const [isDapProcess, setIsDapProcess] = useState(false);
    const [isOpenWarningDialogModal, setIsOpenWarningDialogModal] = useState(false);
    const [hasExistingInsuranceType, setExistingInsuranceType] = useState(false);
    const [hasHealthInsurance, setHealthInsurance] = useState(false);

    const [progressBarCurrentStage, setProgressBarCurrentStage] = useState<
        React.ComponentProps<typeof ProgressBar>["progressBarCurrentStage"]
    >("advice");

    const progressBarStages: React.ComponentProps<
        typeof ProgressBar
    >["progressBarStages"] = [
        {
            sectionTitle: "Why care?",
            sectionTitleMobile: "Why",
            id: "why"
        },
        {
            sectionTitle: "You & your family",
            sectionTitleMobile: "You",
            id: "you"
        },
        {
            sectionTitle: "Your finances",
            sectionTitleMobile: "Finances",
            id: "finances"
        },
        {
            sectionTitle: "Our Advice",
            sectionTitleMobile: "Advice",
            id: "advice"
        }
    ];

    const getRecommendedCount = (recommendations: IRCResults): void => {
        let count = 0;
        for (const recommend in recommendations) {
            const { adviceAmount, hasExceededCover } = recommendations[recommend]?.client;
            if (recommend !== "ultimate" && adviceAmount !== "$0.00") count++;
            else if (
                recommend === "ultimate" &&
                (adviceAmount !== "$0.00" ||
                    (adviceAmount === "$0.00" && hasExceededCover))
            )
                count++;
        }
        setRecommendCount(count);
    };

    const showRecommendation = (recommend): boolean => {
        return (
            (recommend?.client && recommend?.client?.adviceAmount !== "$0.00") ||
            (recommend?.client && recommend?.client?.hasExceededCover)
        );
    };

    const getRecommendedCover = (isProceedModalDone: boolean): void => {
        const sessionUserId = search.has("sessionUserId") && search.get("sessionUserId");
        const visitorId = search.has("visitorId") && search.get("visitorId");
        getFinalRecommendedCover({
            sessionUserId,
            visitorId
        })
            .then((res) => {
                setHealthInsurance(res?.hasHealthInsurance);
                setExistingInsuranceType(res?.hasExistingInsuranceType);
                if (!isProceedModalDone) setIsOpenWarningDialogModal(true);
                if (res.requestCallback) {
                    history.push(`/digital-advice/no-recommendations`);
                    return;
                }
                setRecommendCover(res?.result);
                getRecommendedCount(res?.result);
            })
            .catch(() => {
                useErrorLogger();
                history.push(`/digital-advice/no-recommendations`);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSendSOAPDF = (): void => {
        sendStatementOfAdvice({
            sessionUserId,
            visitorId
        }).catch(() => useErrorLogger());
    };

    const handelGenerateSOAPDF = (): void => {
        if (isSOALoading) return;
        setIsSOALoading(true);
        generateSOAPDF({
            sessionUserId,
            visitorId
        })
            .then((response) => {
                var FileSaver = require("file-saver");
                const linkSource = `data:application/pdf;base64,${response}`;
                FileSaver.saveAs(
                    convertBase64toBlob(linkSource),
                    "StatementOfAdvice.pdf"
                );
            })
            .catch(() => useErrorLogger())
            .finally(() => setIsSOALoading(false));
    };

    const checkRecommendDisclaimer = (): void => {
        const sessionUserId = search.has("sessionUserId") && search.get("sessionUserId");
        const visitorId = search.has("visitorId") && search.get("visitorId");
        getRecommendationDisclaimer({
            sessionUserId,
            visitorId
        })
            .then((res) => {
                if (res?.isDone) handleSendSOAPDF();
                getRecommendedCover(res?.isDone);
            })
            .catch(() => {
                useErrorLogger();
            });
    };

    const recommendationContainerWidth = () => {
        return isTabletPortrait
            ? "100%"
            : !recommendCount
            ? "900px"
            : `${300 * recommendCount}px`;
    };

    useEffect(() => {
        setIsDapProcess(!!window.localStorage.getItem("dap_process"));
        setIsLoading(true);
        checkRecommendDisclaimer();
    }, [isReady]);

    const getRecommendedType = (type: string): IInterimRecommendedCoverResults => {
        return (
            recommendCover && recommendCover.hasOwnProperty(type) && recommendCover[type]
        );
    };

    useEffect(() => {
        abTesting.convertAll(17, "", "", 0);
    }, []);

    return (
        <React.Fragment>
            <DapProceedDialog
                isOpen={isOpenWarningDialogModal}
                hasExistingInsuranceType={hasExistingInsuranceType}
                onClose={() => {
                    handleSendSOAPDF();
                    setIsOpenWarningDialogModal(false);
                }}
            />
            <ProgressBar
                progressBarCurrentStage={progressBarCurrentStage}
                progressBarStages={progressBarStages}
            />
            <div className={cx([styles["our-advice"], styles["layout"]])}>
                {isDapProcess && (
                    <button
                        className={styles["back-btn"]}
                        onClick={() => {
                            handleBack();
                        }}>
                        <Icon
                            size="l"
                            className={cx(styles.icon)}
                            id="ChevronBackLarge"
                        />
                        Back
                    </button>
                )}

                <div className={cx([styles["our-advice-wrapper"]])}>
                    <label className={styles["our-advice-title"]}>
                        Our recommendations
                    </label>

                    {isLoading ? (
                        <p
                            className={cx([
                                styles["our-advice-subtitle-preloader"],
                                styles["pre-loader"]
                            ])}></p>
                    ) : recommendCount > 1 ? (
                        <p className={styles["our-advice-subtitle"]}>
                            Below are {recommendCount === 3 ? "three" : "two"} insurance
                            umbrellas (plans) that we recommend for you. Each plans offers
                            a different level of coverage or protection, based on the
                            information you have given to us.
                        </p>
                    ) : (
                        <p className={styles["our-advice-subtitle"]}>
                            Below is the insurance umbrella (plan) that we recommend for
                            you.
                        </p>
                    )}

                    <div
                        className={styles["our-advice-container"]}
                        style={{
                            width: recommendationContainerWidth()
                        }}>
                        <div className={styles["advice-options"]}>
                            {isLoading && <DapRecommendationPlaceholder />}
                            {!isLoading && (
                                <DapCarousel
                                    showArrows={true}
                                    maxItem={recommendCount}
                                    itemClass={styles["options-item"]}>
                                    {showRecommendation(getRecommendedType("budget")) && (
                                        <div className={styles["height-full"]}>
                                            <RecommendedCover
                                                type={"budget"}
                                                recommendedCover={getRecommendedType(
                                                    "budget"
                                                )}
                                                frequency={frequency}
                                                setFrequency={setFrequency}
                                                hasHealthInsurance={hasHealthInsurance}
                                            />
                                        </div>
                                    )}
                                    {showRecommendation(getRecommendedType("goals")) && (
                                        <div className={styles["height-full"]}>
                                            <RecommendedCover
                                                type={"goals"}
                                                recommendedCover={getRecommendedType(
                                                    "goals"
                                                )}
                                                frequency={frequency}
                                                setFrequency={setFrequency}
                                                isHighlight={true}
                                                hasHealthInsurance={hasHealthInsurance}
                                            />
                                        </div>
                                    )}
                                    {showRecommendation(
                                        getRecommendedType("ultimate")
                                    ) && (
                                        <div className={styles["height-full"]}>
                                            <RecommendedCover
                                                type={"ultimate"}
                                                recommendedCover={getRecommendedType(
                                                    "ultimate"
                                                )}
                                                frequency={frequency}
                                                setFrequency={setFrequency}
                                                hasHealthInsurance={hasHealthInsurance}
                                            />
                                        </div>
                                    )}
                                </DapCarousel>
                            )}
                        </div>
                    </div>

                    <div className={styles["state-of-advice"]}>
                        We have emailed you a copy of your Statement of Advice.
                        <Button
                            width="content"
                            component="button"
                            size="s"
                            onClick={handelGenerateSOAPDF}
                            isSubmitting={isSOALoading}
                            isDisabled={isSOALoading}
                            className={styles["advice-link"]}>
                            <span>{"View Statement (.pdf)"}</span>
                        </Button>
                    </div>

                    <HelpSection
                        title="Need help to choose?"
                        subtext="Talk to us today! A friendly LifeDirect Specialist can answer questions and help you choose an insurer."
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default OurAdvice;
